import { RefObject, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@playerelite/ui";
import { KioskAttractor } from "../../types";
import { attractorTitle } from "./AttractorCampaignCard";

interface Props {
  /** The attractor to confirm deleting. Null closes the dialog. */
  attractor: KioskAttractor | null;
  onClose: () => void;
  /** Rejecting keeps the dialog open and shows the error's message. */
  onConfirm: (attractor: KioskAttractor) => Promise<void>;
  /** Where focus goes after a delete, since the button that opened the dialog is gone. */
  focusAfterDelete: RefObject<HTMLElement>;
}

const DeleteAttractorDialog = ({
  attractor,
  onClose,
  onConfirm,
  focusAfterDelete,
}: Props) => {
  // Kept after `attractor` clears, so the text does not blank out while the dialog animates away.
  const [target, setTarget] = useState(attractor);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string>();
  const deleted = useRef(false);

  useEffect(() => {
    if (attractor) {
      setTarget(attractor);
      setError(undefined);
      deleted.current = false;
    }
  }, [attractor]);

  const confirm = async () => {
    if (!target) return;
    setDeleting(true);
    setError(undefined);
    try {
      await onConfirm(target);
      deleted.current = true;
      onClose();
    } catch (e) {
      setError(
        e instanceof Error && e.message
          ? e.message
          : "Something went wrong. Try again."
      );
    } finally {
      setDeleting(false);
    }
  };

  const title = target ? attractorTitle(target) : "this attractor";

  return (
    <Dialog
      open={attractor !== null}
      onOpenChange={(open) => (!open && !deleting ? onClose() : undefined)}
    >
      <DialogContent
        size="sm"
        onCloseAutoFocus={(e) => {
          if (!deleted.current) return;
          e.preventDefault();
          focusAfterDelete.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>Delete attractor?</DialogTitle>
          <DialogDescription>
            {title} will stop showing on kiosks. This can't be undone.
          </DialogDescription>
        </DialogHeader>
        {error ? (
          <DialogBody>
            <Alert variant="danger" title="Couldn't delete">
              {error}
            </Alert>
          </DialogBody>
        ) : null}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary" disabled={deleting}>
              Cancel
            </Button>
          </DialogClose>
          <Button variant="danger" loading={deleting} onClick={confirm}>
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteAttractorDialog;
